import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { formatDateTime, methodLabel, titleCase } from "../../lib/format";
import type { Payment, PaymentEvent } from "../../lib/types";
import { ErrorNotice, Mono, PageHeader, Sheet, SheetHeader, Skeleton, StatusPill } from "../../components/ui";

interface DetailResponse {
  payment: Payment;
  events: PaymentEvent[] | null;
  /** The snapshot of the pricing rule that produced this payment's fee. */
  pricing: {
    summary?: string;
    model?: string;
    flat_minor?: number;
    percentage_bps?: number;
    flat_component_minor?: number;
    percent_component_minor?: number;
    raw_fee_minor?: number;
    adjustment?: string;
    final_fee_minor?: number;
    plan_name?: string;
    currency?: string;
  };
}

export function PaymentDetail() {
  const { id = "" } = useParams();
  const detail = useQuery({
    queryKey: ["payment", id],
    queryFn: () => api.get<DetailResponse>(`/api/dashboard/payments/${id}`),
  });

  if (detail.isLoading) {
    return (
      <Sheet>
        <Skeleton rows={10} />
      </Sheet>
    );
  }
  if (detail.error) return <ErrorNotice error={detail.error} />;

  const payment = detail.data!.payment;
  const events = detail.data?.events ?? [];
  const pricing = detail.data?.pricing ?? {};

  return (
    <>
      <Link
        to="/payments"
        className="mb-4 inline-flex items-center gap-1.5 text-[12.5px] text-ink-muted transition-colors hover:text-ink"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M7.5 2.5 4 6l3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        All payments
      </Link>

      <PageHeader
        eyebrow={payment.reference}
        title={payment.charged.formatted + " " + payment.currency}
        description={payment.item_name}
        actions={<StatusPill status={payment.status} />}
      />

      {payment.failure_message && (
        <div className="settle mb-6 rounded-md border border-sienna/25 bg-sienna-wash px-4 py-3">
          <p className="text-[13px] font-medium text-sienna">{payment.failure_message}</p>
          {payment.failure_code && (
            <p className="mt-0.5 font-mono text-[11.5px] text-sienna/75">{payment.failure_code}</p>
          )}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="space-y-6">
          {/* The money breakdown is the first thing on the page: a merchant opening a
              payment almost always wants to know what they were charged and why. */}
          <Sheet className="settle settle-1 overflow-hidden">
            <SheetHeader title="Money" description="How the amount was split" />
            <dl className="divide-y divide-rule">
              <MoneyRow label="Customer was charged" value={payment.charged.formatted} currency={payment.currency} strong />
              <MoneyRow label="You asked to collect" value={payment.amount.formatted} currency={payment.currency} />
              <MoneyRow
                label="Bank fee (ZB)"
                value={`−${payment.provider_fee.formatted}`}
                currency={payment.currency}
                muted
              />
              <MoneyRow
                label="Platform fee (PayFlow)"
                value={`−${payment.platform_fee.formatted}`}
                currency={payment.currency}
                accent="brass"
              />
              <MoneyRow
                label={payment.status === "succeeded" ? "Settled to you" : "Would settle to you"}
                value={payment.net.formatted}
                currency={payment.currency}
                strong
                accent="verdigris"
              />
            </dl>

            {pricing.summary && (
              <div className="border-t border-rule bg-paper-sunk/50 px-5 py-3">
                <p className="label mb-1">How this fee was calculated</p>
                <p className="text-[12.5px] leading-relaxed text-ink-muted">{pricing.summary}</p>
                {pricing.plan_name && (
                  <p className="mt-1 text-[11.5px] text-ink-faint">
                    Plan: {pricing.plan_name}
                    {payment.fee_bearer === "customer" && " · surcharged to the customer"}
                  </p>
                )}
              </div>
            )}
          </Sheet>

          <Sheet className="settle settle-2 overflow-hidden">
            <SheetHeader title="History" description="Every change, in order" />
            {events.length === 0 ? (
              <p className="px-5 py-6 text-[13px] text-ink-muted">No events recorded.</p>
            ) : (
              <ol className="px-5 py-4">
                {events.map((event, i) => (
                  <li key={event.ID} className="relative flex gap-3 pb-4 last:pb-0">
                    {/* A vertical rule connecting the entries, stopping at the last. */}
                    {i < events.length - 1 && (
                      <span
                        className="absolute left-[3.5px] top-3 h-full w-px bg-rule"
                        aria-hidden="true"
                      />
                    )}
                    <span
                      className="relative mt-[5px] h-2 w-2 shrink-0 rounded-full bg-verdigris"
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] text-ink">{titleCase(event.Type.split(".").pop())}</p>
                      <p className="mt-0.5 text-[11.5px] text-ink-faint">
                        {formatDateTime(event.CreatedAt)}
                        {event.Source && ` · ${sourceLabel(event.Source)}`}
                        {event.ToStatus && event.FromStatus && event.ToStatus !== event.FromStatus && (
                          <> · {event.FromStatus.replace(/_/g, " ")} → {event.ToStatus.replace(/_/g, " ")}</>
                        )}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Sheet>
        </div>

        <div className="space-y-6">
          <Sheet className="settle settle-3 overflow-hidden">
            <SheetHeader title="Details" />
            <dl className="divide-y divide-rule text-[13px]">
              <DetailRow label="Payment ID" value={<Mono copyable>{payment.id}</Mono>} />
              <DetailRow label="Your reference" value={<Mono copyable>{payment.reference}</Mono>} />
              {payment.provider_reference && (
                <DetailRow label="Bank reference" value={<Mono copyable>{payment.provider_reference}</Mono>} />
              )}
              <DetailRow label="Method" value={methodLabel(payment.method)} />
              <DetailRow label="Mode" value={titleCase(payment.mode)} />
              <DetailRow label="Created" value={formatDateTime(payment.created_at)} />
              {payment.succeeded_at && (
                <DetailRow label="Succeeded" value={formatDateTime(payment.succeeded_at)} />
              )}
              {payment.available_at && (
                <DetailRow
                  label="Available for payout"
                  value={formatDateTime(payment.available_at)}
                />
              )}
            </dl>
          </Sheet>

          {(payment.payer.email || payment.payer.mobile || payment.payer.first_name) && (
            <Sheet className="settle settle-4 overflow-hidden">
              <SheetHeader title="Customer" />
              <dl className="divide-y divide-rule text-[13px]">
                {(payment.payer.first_name || payment.payer.last_name) && (
                  <DetailRow
                    label="Name"
                    value={[payment.payer.first_name, payment.payer.last_name].filter(Boolean).join(" ")}
                  />
                )}
                {payment.payer.email && <DetailRow label="Email" value={payment.payer.email} />}
                {payment.payer.mobile && (
                  <DetailRow label="Mobile" value={<Mono>{payment.payer.mobile}</Mono>} />
                )}
              </dl>
            </Sheet>
          )}

          {payment.metadata && Object.keys(payment.metadata).length > 0 && (
            <Sheet className="settle settle-5 overflow-hidden">
              <SheetHeader title="Your metadata" />
              <dl className="divide-y divide-rule text-[13px]">
                {Object.entries(payment.metadata).map(([key, value]) => (
                  <DetailRow key={key} label={key} value={<Mono>{value}</Mono>} />
                ))}
              </dl>
            </Sheet>
          )}
        </div>
      </div>
    </>
  );
}

function MoneyRow({
  label,
  value,
  currency,
  strong,
  muted,
  accent,
}: {
  label: string;
  value: string;
  currency: string;
  strong?: boolean;
  muted?: boolean;
  accent?: "brass" | "verdigris";
}) {
  const colour =
    accent === "brass"
      ? "text-brass"
      : accent === "verdigris"
        ? "text-verdigris"
        : muted
          ? "text-ink-muted"
          : "text-ink";

  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-3">
      <dt className={`text-[13px] ${muted ? "text-ink-muted" : "text-ink"}`}>{label}</dt>
      <dd className={`font-mono tabular ${strong ? "text-[15px] font-medium" : "text-[13px]"} ${colour}`}>
        {value} <span className="text-ink-faint">{currency}</span>
      </dd>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-2.5">
      <dt className="shrink-0 text-ink-muted">{label}</dt>
      <dd className="min-w-0 truncate text-right">{value}</dd>
    </div>
  );
}

function sourceLabel(source: string): string {
  const labels: Record<string, string> = {
    api: "via API",
    zb_callback: "bank notification",
    reconciler: "reconciliation",
    poller: "status check",
    admin: "operator",
    system: "system",
  };
  return labels[source] ?? source;
}
